import {useIsBotModeEnabled} from '@aboutyou/data-fetcher';
import {DeepLinkLoaderPage} from '@aboutyou/logic-components';
import {Redirect, Routes} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React from 'react';
import {AdventsCalendar} from '../bridge/pages/AdventsCalendar';
import {BrandsOverview} from '../bridge/pages/BrandsOverview';
import {DropDetailPage} from '../bridge/pages/DropDetailPage';
import {DropsOverviewPage} from '../bridge/pages/DropsOverviewPage';
import {LegalPage} from '../bridge/pages/LegalPage';
import {LiveShoppingPage} from '../bridge/pages/LiveShopping';
import {OutfitDetailPage} from '../bridge/pages/OutfitDetailPage';
import {OutfitOverviewPage} from '../bridge/pages/OutfitOverviewPage';
import {PremiumPage} from '../bridge/pages/PremiumPage';
import {ProductReviewsPage} from '../bridge/pages/ProductReviewsPage';
import {SeoStreamPage} from '../bridge/pages/SeoStreamPage';
import {StoryOverviewPage} from '../bridge/pages/StoryOverviewPage';
import {TrackAndTrace} from '../bridge/pages/TrackAndTrace';
import {WelcomePage} from '../bridge/pages/WelcomePage';
import {CustomerBankData} from '../bridge/pages/account/CustomerBankData';
import {FavouriteBrandsPage} from '../bridge/pages/account/FavouriteBrandsPage';
import {InitiateReturnPage} from '../bridge/pages/account/InitiateReturnPage';
import {OrderDetailPage} from '../bridge/pages/account/OrderDetailPage';
import {OrdersPage} from '../bridge/pages/account/OrdersPage';
import {PasswordReset} from '../bridge/pages/account/PasswordReset';
import {SettingsPage} from '../bridge/pages/account/SettingsPage';
import {UserProfile} from '../bridge/pages/account/UserProfile';
import {ArticlePage as ArticlePageLegacy} from '../bridge/pages/help-center/ArticlePage';
import {ContactForm} from '../bridge/pages/help-center/ContactForm';
import {LandingPage} from '../bridge/pages/help-center/LandingPage';
import {SearchResults as HelpCenterSearchResult} from '../bridge/pages/help-center/SearchResults';
import {SectionPage as SectionPageLegacy} from '../bridge/pages/help-center/SectionPage';
import {BlackFriday} from '../bridge/pages/seo/BlackFriday';
import {AboutUs} from './components/pages/AboutUs/AboutUs';
import {ArticleDetailSwitch} from './components/pages/ArticleDetailPage/ArticleDetailSwitch';
import {BasketPage} from './components/pages/BasketPage/BasketPage';
import {BrandPageLegacySwitch} from './components/pages/BrandPage/BrandPageLegacySwitch';
import {BrandShopPage} from './components/pages/BrandPage/BrandShopPage';
import {CampaignConfirmationPage} from './components/pages/CampaignConfirmationPage/CampaignConfirmationPage';
import {Category} from './components/pages/CategoryPage/Category';
import {CategoryLegacy} from './components/pages/CategoryPage/CategoryLegacy';
import {CheckoutPage} from './components/pages/CheckoutPage/CheckoutPage';
import {CouponLandingPageSwitch} from './components/pages/CouponLandingPage/CouponLandingPageSwitch';
import {CustomPage} from './components/pages/CustomPage/CustomPage';
import {DeclarationOfLoss} from './components/pages/DeclarationOfLoss/DeclarationOfLoss';
import {GiftCardRefundPage} from './components/pages/GiftCardRefundPage/GiftCardRefundPage';
import {ArticlePage} from './components/pages/HelpCenter/ArticlePage';
import {SectionPage} from './components/pages/HelpCenter/SectionPage';
import {HomePage} from './components/pages/HomePage/HomePage';
import {LaunchTeaser} from './components/pages/LaunchTeaserPage/LaunchTeaser';
import {LegalReport} from './components/pages/LegalReport/LegalReport';
import {SubscriptionConfirmationLandingPage} from './components/pages/NewsletterSubscription/SubscriptionConfirmationLandingPage';
import {UnsubscribeConfirmationLandingPage} from './components/pages/NewsletterSubscription/UnsubscribeConfirmationLandingPage';
import {UnsubscribeLandingPage} from './components/pages/NewsletterSubscription/UnsubscribeLandingPage';
import {NotFound} from './components/pages/NotFoundPage/NotFound';
import {OrderSuccessPage} from './components/pages/OrderSuccessPage/OrderSuccessPage';
import {PayOrderAppForceLogin} from './components/pages/PayOrderAppForceLogin/PayOrderAppForceLogin';
import {SearchResultsPage} from './components/pages/SearchResultsPage/SearchResultsPage';
import {SimilarProductsPage} from './components/pages/SimilarProductsPage/SimilarProductsPage';
import {SportswearPage} from './components/pages/SportswearPage/SportswearPage';
import {StoryClusterPage} from './components/pages/StoryClusterPage/StoryClusterPage';
import {StoryPage} from './components/pages/StoryPage/StoryPage';
import {StreetwearPage} from './components/pages/StreetwearPage/StreetwearPage';
import {WishlistPage} from './components/pages/WishlistPage/WishlistPage';

export const RouteComponents: React.FC = () => {
  const isBotMode = useIsBotModeEnabled();

  return (
    /**
     * The "id=page-content-wrapper" is used to alter the min-height from a page level
     * by adding extra css to this id.
     * This avoids having the PageContentWrapper being moved to page level or passing down
     * callbacks to achieve page specific behavior.
     *
     * @see 'packages/core/application-desktop/src/bridge/pages/Checkout.eager.tsx'
     */
    <PageContentWrapper id="page-content-wrapper" $withMinHeight={!isBotMode}>
      <Routes
        entries={{
          404: {component: <NotFound />},
          account: {component: <Redirect to={'/a/profile'} />},
          accountOverview: {component: <Redirect to={'/a/profile'} />},
          userProfile: {component: <UserProfile />},
          adventsCalendar: {component: <AdventsCalendar />},
          articleDetail: {component: <ArticleDetailSwitch />},
          articleDetailSoldOut: {component: <ArticleDetailSwitch />},
          bankData: {component: <CustomerBankData />},
          basket: {component: <BasketPage />},
          brandShop: {component: <BrandShopPage />},
          brandLegacy: {component: <BrandPageLegacySwitch />},
          brandsOverview: {component: <BrandsOverview />},
          category: {component: <Category />},
          categoryLegacy: {component: <CategoryLegacy />},
          chatbot: {component: <Redirect to={'/h#chatbot'} />},
          customPage: {component: <CustomPage />},
          aboutUs: {component: <AboutUs />},
          premium: {component: <PremiumPage />},
          checkout: {component: <CheckoutPage />},
          contacts: {component: <ContactForm />},
          couponLandingPage: {
            component: <CouponLandingPageSwitch />,
          },
          couponWallet: {component: <DeepLinkLoaderPage openCouponWallet />},
          coinsWallet: {component: <DeepLinkLoaderPage openCoinsWallet />},
          coinsHistory: {component: <DeepLinkLoaderPage openCoinsHistory />},
          dropsOverview: {component: <DropsOverviewPage />},
          dropDetail: {component: <DropDetailPage />},
          favourites: {component: <FavouriteBrandsPage />},
          declarationOfLoss: {component: <DeclarationOfLoss />},
          legalReport: {component: <LegalReport />},
          helpCenterArticlePageLegacy: {component: <ArticlePageLegacy />},
          helpCenterLandingPageLegacy: {component: <LandingPage />},
          helpCenterSearchResultPageLegacy: {
            component: <HelpCenterSearchResult />,
          },
          helpCenterSectionPageLegacy: {component: <SectionPageLegacy />},
          helpCenterSectionRedirect: {component: <Redirect to={'/h'} />},
          helpCenterSectionPage: {component: <SectionPage />},
          helpCenterArticlePage: {component: <ArticlePage />},
          home: {component: <HomePage />},
          legal: {component: <LegalPage />},
          notFound: {component: <NotFound />},
          newsletter: {component: <Redirect to={'/'} />},
          newsletterSuccess: {
            component: <Redirect to={'/'} />,
          },
          orderDetails: {component: <OrderDetailPage />},
          orderReturn: {component: <InitiateReturnPage />},
          orderSuccess: {component: <OrderSuccessPage />},
          ordersOverview: {component: <OrdersPage />},
          outfitDetail: {component: <OutfitDetailPage />},
          outfitOverview: {component: <OutfitOverviewPage />},
          passwordReset: {component: <PasswordReset />},
          search: {component: <SearchResultsPage />},
          searchResults: {component: <SearchResultsPage />},
          seoStream: {component: <SeoStreamPage />},
          settings: {
            component: <SettingsPage />,
          },
          settingsChooseShop: {component: <Redirect to={'/'} />},
          streetwear: {component: <StreetwearPage />},
          sportswear: {component: <SportswearPage />},
          static: {component: <LegalPage />},
          story: {component: <StoryPage />},
          storyOverview: {component: <StoryOverviewPage />},
          storyCluster: {component: <StoryClusterPage />},
          subscriptionConfirmation: {
            component: <SubscriptionConfirmationLandingPage />,
          },
          unsubscribeLandingPage: {component: <UnsubscribeLandingPage />},
          unsubscribeConfirmationLandingPage: {
            component: <UnsubscribeConfirmationLandingPage />,
          },
          trackAndTrace: {component: <TrackAndTrace />},
          voucherPage: {component: <Redirect to={'/'} />},
          welcome: {component: <WelcomePage />},
          wishlist: {component: <WishlistPage />},
          productReviews: {component: <ProductReviewsPage />},
          liveShopping: {component: <LiveShoppingPage />},
          teaserPage: {component: <LaunchTeaser />},
          blackFridayPage: {component: <BlackFriday />},
          similarProducts: {component: <SimilarProductsPage />},
          giftCardRefund: {component: <GiftCardRefundPage />},
          campaignConfirmationPage: {component: <CampaignConfirmationPage />},
          newsletterSubscription: {
            component: <DeepLinkLoaderPage openNewsletter />,
          },
          payOrderApp: {component: <PayOrderAppForceLogin />},
          DEV_appCheckoutUrl: {component: <Redirect to={'/'} />},
        }}
      />
    </PageContentWrapper>
  );
};

// Giving a min height with height of Header(+ Campaign Banner)
// This is important to prevent CLS on loading states to push the footer below the fold
const PageContentWrapper = styled.div<{$withMinHeight: boolean}>`
  min-height: ${({$withMinHeight}) =>
    $withMinHeight ? 'calc(100vh - (188px + 44px))' : 'unset'};
`;
